import {useCallback} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {LATEST_ATR, REQUEST_ATR, SUBMIT_ATR, UPDATE_ATR} from './AtrQueries';
import {LatestAtr, LatestAtrVariables} from '../../schema-types/LatestAtr';
import {UpdateAtr, UpdateAtrVariables} from '../../schema-types/UpdateAtr';
import {RequestAtr, RequestAtrVariables} from '../../schema-types/RequestAtr';
import LIST_CONTACTS from '../AccountContactsGrid/accountContactQueries';
import {cleanForCuro} from '../../utils/cleanString';
import validateUUID from '../../utils/validateUUID';
import {getAtrSubmitHandler} from './AtrFields';
import {ReviewDetailsFragment_contacts} from '../../schema-types/ReviewDetailsFragment';
import {SubmitAtr, SubmitAtrVariables} from '../../schema-types/SubmitAtr';

const getErrorMessage = (errors: (string | null)[], defaultMessage: string): string => {
  if (errors && errors.length > 0 && typeof errors[0] === 'string') {
    return errors[0];
  }
  return defaultMessage;
};

export const useATR = (
  accountId: string,
  contact?: ReviewDetailsFragment_contacts,
  close?: () => void
) => {
  const {enqueueSnackbar} = useSnackbar();
  const {loading, data, error} = useQuery<LatestAtr, LatestAtrVariables>(LATEST_ATR, {
    // pollInterval: 10000,
    variables: {
      contactId: validateUUID(contact?.id)!,
      accountId: validateUUID(accountId)!,
    },
    skip: !contact?.id || !accountId,
  });

  const [requestAtrMutation] = useMutation<RequestAtr, RequestAtrVariables>(REQUEST_ATR, {
    refetchQueries: [
      {
        query: LIST_CONTACTS,
        variables: {
          accountId: validateUUID(accountId)!,
        },
      },
      {
        query: LATEST_ATR,
        variables: {
          contactId: validateUUID(contact?.id),
          accountId,
        },
      },
    ],
    onCompleted: ({requestAtr: requestAtrData}) => {
      if (!requestAtrData?.ok) {
        const defaultMessage = 'Unable to request for the contact to complete an ATR';
        const message = getErrorMessage(requestAtrData?.errors || [], defaultMessage);
        return enqueueSnackbar(message, {variant: 'error'});
      }
      close?.();
      return enqueueSnackbar(
        'A request for the contact to complete an ATR has been sent successfully',
        {
          variant: 'success',
        }
      );
    },
    onError: (err) => {
      return enqueueSnackbar(
        `Unable to request for the contact to complete an ATR: ${err.message}`,
        {
          variant: 'error',
          autoHideDuration: null,
        }
      );
    },
  });
  const [updateAtrMutation] = useMutation<UpdateAtr, UpdateAtrVariables>(UPDATE_ATR, {
    refetchQueries: [
      {
        query: LIST_CONTACTS,
        variables: {
          accountId: validateUUID(accountId)!,
        },
      },
      {
        query: LATEST_ATR,
        variables: {
          contactId: validateUUID(contact?.id),
          accountId,
        },
      },
    ],
    onCompleted: ({updateAtr: updateAtrData}) => {
      if (!updateAtrData?.ok) {
        const defaultMessage = 'Unable to update the ATR';
        const message = getErrorMessage(updateAtrData?.errors || [], defaultMessage);
        return enqueueSnackbar(message, {variant: 'error'});
      }
      close?.();
      return enqueueSnackbar('The ATR has been updated', {
        variant: 'success',
      });
    },
    onError: (err) => {
      enqueueSnackbar(`Unable to update the ATR. ${err.message}`, {
        variant: 'error',
        autoHideDuration: null,
      });
    },
  });
  const [submitAtrMutation] = useMutation<SubmitAtr, SubmitAtrVariables>(SUBMIT_ATR, {
    onCompleted: ({submitAtr: submitAtrData}) => {
      if (!submitAtrData?.ok) {
        const defaultMessage = 'Unable to submit';
        const message = getErrorMessage(submitAtrData?.errors || [], defaultMessage);
        return enqueueSnackbar(message, {variant: 'error'});
      }
      close?.();
      return enqueueSnackbar('Submitted the ATR', {variant: 'success'});
    },
    onError: (err) => {
      enqueueSnackbar(`Unable to submit the ATR. ${err.message}`, {
        variant: 'error',
        autoHideDuration: null,
      });
    },
  });

  const getSubmitHandler: getAtrSubmitHandler = useCallback(
    (flags?) => (values) => {
      if (flags?.action === 'curo') {
        // Submitting to CURO
        return submitAtrMutation({
          variables: {
            atrId: String(data?.latestAtr?.id),
          },
        });
      }

      if (flags?.action === 'manual') {
        // Manual input of an ATR score
        return updateAtrMutation({
          variables: {
            atrId: String(data?.latestAtr?.id),
            amendedValue: values.amendedValue,
            amendReason: cleanForCuro(values.amendReason),
          },
        });
      }

      // Requesting a questionnaire for contact(s)
      if (validateUUID(contact?.id)) {
        return requestAtrMutation({
          variables: {
            staffId: values.adviserId,
            contactId: validateUUID(contact?.id)!,
            accountId: validateUUID(accountId)!,
          },
        });
      }
    },
    [requestAtrMutation, updateAtrMutation, submitAtrMutation, contact, accountId]
  );

  return {
    latestATRLoading: loading,
    latestATRData: data,
    latestATRError: error,
    getSubmitHandler,
    updateAtrMutation,
    submitAtrMutation,
  };
};
